/**
 * OtisChat — the reusable Otis conversation surface.
 *
 * Self-contained: manages its own messages / input / pending state and talks to
 * the `askTheo` callable. Used by BOTH the full page (`/home/ask`) and the
 * floating `OtisWidget`, so the chat behaves identically everywhere. The
 * surrounding chrome (page header / widget panel header) is the caller's job —
 * this renders the scrolling thread + composer and fills its parent.
 *
 * @see functions/src/ai/askTheo.ts
 * @see src/services/ask-theo.client.ts
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Send,
  Loader2,
  AlertCircle,
  User as UserIcon,
  ExternalLink,
  KeyRound,
  Home,
  Boxes,
} from 'lucide-react';
import { MarkdownDisplay } from '@/components/MarkdownDisplay';
import { useTenant } from '@/hooks/useTenant';
import {
  askTheo,
  type AskTheoCitation,
  type AskTheoTurn,
} from '@/services/ask-theo.client';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  citations?: AskTheoCitation[];
}

const EXAMPLE_PROMPTS: { icon: typeof Home; text: string }[] = [
  { icon: Home, text: "What's Louise Houston's Airbnb called?" },
  { icon: KeyRound, text: "What's the password for the printer at Berida?" },
  { icon: Boxes, text: 'What equipment is at 4 Songline Place?' },
];

const SOURCE_LABELS: Record<string, string> = {
  pipedrive: 'Pipedrive',
  slack: 'Slack',
  fieldpulse: 'FieldPulse',
  theo: 'Theo',
};

/** Otis's friendly face — a simple character, not a robot. */
export function OtisFace({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
      <circle cx="9" cy="10" r="1.5" fill="white" />
      <circle cx="15" cy="10" r="1.5" fill="white" />
      <path
        d="M8.4 14.4c1 1.4 2.2 2.1 3.6 2.1s2.6-.7 3.6-2.1"
        stroke="white"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Otis's gradient avatar. */
export function OtisAvatar({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const box = size === 'lg' ? 'w-14 h-14' : size === 'sm' ? 'w-7 h-7' : 'w-9 h-9';
  const face = size === 'lg' ? 'w-9 h-9' : size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
  return (
    <div
      className={`${box} shrink-0 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/25`}
    >
      <OtisFace className={face} />
    </div>
  );
}

export function OtisChat({ className = '' }: { className?: string }) {
  const { tenant } = useTenant();
  const companyName = tenant?.companyName || 'the company';

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [longWait, setLongWait] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pending, longWait]);

  // After 30s of waiting, reassure the user a big report is being gathered.
  useEffect(() => {
    if (!pending) {
      setLongWait(false);
      return;
    }
    const t = setTimeout(() => setLongWait(true), 30_000);
    return () => clearTimeout(t);
  }, [pending]);

  async function send(text: string) {
    const question = text.trim();
    if (!question || pending) return;

    setError(null);
    setInput('');

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: question }];
    setMessages(nextMessages);
    setPending(true);

    const turns: AskTheoTurn[] = nextMessages.map((m) => ({ role: m.role, content: m.content }));

    try {
      const res = await askTheo(turns);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.answer || "Hmm — I couldn't find anything on that one.",
          citations: res.citations,
        },
      ]);
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code;
      const message =
        code === 'functions/permission-denied'
          ? "Looks like your account doesn't have access to me yet — ask an admin."
          : code === 'functions/resource-exhausted'
            ? "I'm a bit swamped right now — give me a moment and try again."
            : code === 'functions/deadline-exceeded'
              ? 'That one took too long to chase down — try narrowing it a touch.'
              : (err as Error)?.message || 'Something went wrong on my end. Try again?';
      setError(message);
      setMessages((prev) => prev.slice(0, -1));
      setInput(question);
    } finally {
      setPending(false);
      inputRef.current?.focus();
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  }

  const empty = messages.length === 0;

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`}>
      {/* Thread */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {empty ? (
          <OtisGreeting companyName={companyName} />
        ) : (
          messages.map((m, i) => <MessageBubble key={i} message={m} />)
        )}

        {pending && (
          <div className="flex items-start gap-3 pl-1">
            <OtisAvatar size="sm" />
            <div className="text-theme-tertiary text-sm">
              <div className="flex items-center gap-2">
                <span className="flex gap-1">
                  <Dot delay={0} />
                  <Dot delay={0.15} />
                  <Dot delay={0.3} />
                </span>
                Otis is digging through the archive…
              </div>
              {longWait && (
                <motion.p
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-1.5 text-xs text-amber-400/90"
                >
                  This is taking a while — must be a lot to gather. Hang tight…
                </motion.p>
              )}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mx-3 mb-2 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Composer */}
      <div className="shrink-0 p-3 border-t border-theme">
        {empty && (
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {EXAMPLE_PROMPTS.map((ex) => {
              const Icon = ex.icon;
              return (
                <button
                  key={ex.text}
                  onClick={() => void send(ex.text)}
                  disabled={pending}
                  className="inline-flex items-center gap-1.5 text-xs text-theme-secondary rounded-full border border-theme bg-surface-hover px-3 py-1.5 hover:border-amber-500/50 hover:text-theme-primary transition-colors disabled:opacity-50"
                >
                  <Icon className="w-3 h-3 text-amber-400 shrink-0" />
                  {ex.text}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-end gap-2 rounded-2xl border border-theme bg-surface-card p-2 focus-within:border-amber-500/50 transition-colors">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder="Ask Otis anything…"
            disabled={pending}
            className="flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none max-h-28"
            style={{ minHeight: '2rem' }}
          />
          <button
            onClick={() => void send(input)}
            disabled={pending || !input.trim()}
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 transition-all"
            aria-label="Send"
          >
            {pending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="mt-1.5 text-[11px] text-theme-muted text-center">
          Otis can be wrong — double-check anything important.
        </p>
      </div>
    </div>
  );
}

function Dot({ delay }: { delay: number }) {
  return (
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"
      animate={{ opacity: [0.3, 1, 0.3], y: [0, -2, 0] }}
      transition={{ duration: 0.9, repeat: Infinity, delay }}
    />
  );
}

function OtisGreeting({ companyName }: { companyName: string }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center py-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 18 }}
      >
        <OtisAvatar size="lg" />
      </motion.div>
      <motion.h2
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-4 text-xl font-bold text-theme-primary"
      >
        Hi, I'm Otis 👋
      </motion.h2>
      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.18 }}
        className="mt-1.5 text-sm text-theme-tertiary max-w-md px-2"
      >
        I'm {companyName}'s memory. I've read the lot — everything in Theo (jobs, deals, faults,
        customers) <span className="text-theme-secondary font-medium">plus years of our old
        Pipedrive deals and FieldPulse job notes</span>. Ask me about a customer, a site, a past
        visit, even a password we jotted down once.
      </motion.p>
    </div>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
    >
      {isUser ? (
        <div className="shrink-0 w-7 h-7 rounded-full bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
          <UserIcon className="w-4 h-4 text-blue-400" />
        </div>
      ) : (
        <OtisAvatar size="sm" />
      )}
      <div className={`flex-1 min-w-0 ${isUser ? 'flex justify-end' : ''}`}>
        <div
          className={`inline-block max-w-full rounded-2xl px-4 py-2.5 ${
            isUser
              ? 'bg-blue-500/15 text-theme-primary'
              : 'bg-surface-card border border-theme text-theme-secondary'
          }`}
        >
          {!isUser && <p className="text-[11px] font-semibold text-amber-400 mb-1">Otis</p>}
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          ) : (
            <MarkdownDisplay content={message.content} />
          )}
          {!isUser && message.citations && message.citations.length > 0 && (
            <Citations citations={message.citations} />
          )}
        </div>
      </div>
    </motion.div>
  );
}

function Citations({ citations }: { citations: AskTheoCitation[] }) {
  const sorted = [...citations].sort((a, b) => a.id - b.id);
  return (
    <div className="mt-3 pt-3 border-t border-theme">
      <p className="text-[11px] uppercase tracking-wide text-theme-muted mb-1.5">Sources</p>
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((c) => {
          const label = `[${c.id}] ${SOURCE_LABELS[c.source] || c.source} · ${c.title}`;
          const chip = (
            <span className="inline-flex items-center gap-1 max-w-[280px] truncate rounded-full border border-theme bg-surface-hover px-2.5 py-1 text-[11px] text-theme-tertiary hover:text-theme-primary hover:border-amber-500/40 transition-colors">
              <span className="truncate">{label}</span>
              {c.link && <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />}
            </span>
          );
          return c.link && c.link.startsWith('/') ? (
            <Link key={c.id} to={c.link} title={c.title}>
              {chip}
            </Link>
          ) : (
            <span key={c.id} title={c.title}>
              {chip}
            </span>
          );
        })}
      </div>
    </div>
  );
}
